import React, { useState, useEffect, useRef, useCallback } from 'react';
import chatBubble from '../../images/chatBubble.svg';
import { UnreadBadge } from "./UnreadBadge";
import { ChatModal } from "./ChatModal";
import usePortalService from "../../services/PortalService";
import { UseHub } from "../../hooks/hub.hook";
import { Dialog, Message } from '../../types';

type RecieveMessageResponseProps = {
    dialogId: number,
    message: Message,
    success: boolean
};

export const Chat = () => {
    const { getDialog, getUserDialogs, getMessagesFromDialog } = usePortalService();

    const [isChatOpen, setChatOpen] = useState(false);
    const [dialogs, setDialogs] = useState<Dialog[]>([]);
    const [messages, setMessages] = useState<Message[]>([]);
    const [lastReadMessageId, setLastReadMessageId] = useState(-1);
    const [interlocutorId, setInterlocutorId] = useState(-1);
    const [activeDialogId, setActiveDialogId] = useState(-1);

    // Refs для управления очередью и состоянием отправки
    const activeDialogIdRef = useRef(-1);
    const isSendingRef = useRef(false);
    const sendingMessagesRef = useRef<{ RecipientId: number, text: string }[]>([]);
    const currentMessageRef = useRef<any>(null);

    // Синхронизация ref с state для использования в колбэках
    useEffect(() => {
        activeDialogIdRef.current = activeDialogId;
    }, [activeDialogId]);

    const updateDialogs = useCallback((message: Message, dialogId: number) => {
        setDialogs(prevDialogs => {
            const existingDialogIndex = prevDialogs.findIndex(d => d.dialogId === dialogId);

            if (existingDialogIndex !== -1) {
                return prevDialogs.map((d, index) => {
                    if (index === existingDialogIndex) {
                        return {
                            ...d,
                            countOfUnreadMessage: message.isMine ? 0 : (d.countOfUnreadMessage || 0) + 1,
                            messageText: message.text,
                            dateTime: message.dateTime
                        };
                    }
                    return d;
                });
            }

            return prevDialogs;
        });
    }, []);

    const sendNext = useCallback(() => {
        if (sendingMessagesRef.current.length > 0) {
            if (UseHub.checkConnectionState()) {
                currentMessageRef.current = sendingMessagesRef.current.shift();
                isSendingRef.current = true;
                UseHub.invoke("SendMessage", currentMessageRef.current);
            } else {
                UseHub.reconnect();
                setTimeout(sendNext, 1000);
            }
        } else {
            isSendingRef.current = false;
            currentMessageRef.current = null;
        }
    }, []);

    const addToMessageQueue = (text: string) => {
        const message = {
            RecipientId: interlocutorId,
            text
        };
        sendingMessagesRef.current.push(message);
        if (!isSendingRef.current) {
            sendNext();
        }
    };

    const receiveMyMessage = (receiveMessageResponse: RecieveMessageResponseProps) => {
        const { message, dialogId } = receiveMessageResponse;
        
        if (activeDialogIdRef.current === dialogId) {
            setLastReadMessageId(message.id);

            const myMsg = { ...message, isMine: true };
            setMessages(prev => [...prev, myMsg]);
        }
        updateDialogs({ ...message, isMine: true }, dialogId);
        sendNext();
    };

    const receiveMessage = (receiveMessageResponse: RecieveMessageResponseProps) => {
        const { message, dialogId, success } = receiveMessageResponse;
        if (success) {
            updateDialogs(message, dialogId);
            if (activeDialogIdRef.current === dialogId) {
                setMessages(prev => [...prev, message]);
            }
        }
    };

    const handleOpenDialog = (dialogId: number, interlocutorId: number) => {
        setInterlocutorId(interlocutorId);
        setActiveDialogId(dialogId);
        getMessagesFromDialog(dialogId).then(result => {
            setMessages(result.messages.items);
            setLastReadMessageId(result.mineLastReadMessage);
        });
    };

    const handleClose = () => {
        setMessages([]);
        setActiveDialogId(-1);
        setChatOpen(false);
    };

    const handleRead = (messageId: number, unreadMessagesCount: number) => {
        UseHub.invoke("ReadMessage", {
            messageId,
            dialogId : activeDialogId
        });

        setDialogs(prevDialogs => {
            return prevDialogs.map(d => {
                if (d.dialogId === activeDialogId) {
                    return {
                        ...d,
                        countOfUnreadMessage: unreadMessagesCount
                    };
                }
                return d;
            });
        });
    };

    useEffect(() => {
        UseHub.connect();
        UseHub.addHandler("ReceiveSendMessage", receiveMessage);
        UseHub.addHandler("ReceiveMySendMessage", receiveMyMessage);
        return () => {
            UseHub.disconnect();
        };
    }, [updateDialogs, sendNext]);

    useEffect(() => {
        getUserDialogs().then(result => {
            if (result.success && result.dialogs.items) {
                setDialogs(result.dialogs.items);
            }
        });
    }, []);

    return (
        <>
            <UnreadBadge
                count={dialogs.filter(dialog => dialog.countOfUnreadMessage !== 0).length}
                overflow={99}
                style={{
                    minWidth: '17px', 
                    height: '17px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    padding: '0 5px'
                }}
            >
                <img
                    src={chatBubble}
                    onClick={() => setChatOpen(true)}
                    style={{ cursor: 'pointer' }}
                />
            </UnreadBadge>
            <ChatModal
                isOpen={isChatOpen}
                onClose={handleClose}
                dialogs={dialogs}
                messages={messages}
                lastReadMessageId={lastReadMessageId}
                activeDialogId={activeDialogId}
                onOpenDialog={handleOpenDialog}
                onSendMessage={addToMessageQueue}
                onRead={handleRead}
            />
        </>
    );
};